<script setup lang="ts">
import { ref, computed, h, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import { TabMixins } from '../mixin';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useGatewayStore } from '@/store/GatewayStore';
import { useDestinationStore } from '@/store/DestinationStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useDurationStore } from '@/store/DurationStore';
import { useHotelStore } from '@/store/HotelStore';
import { useTabComposable } from '../useTabComposable';
import { getTextVariant } from '@/utils/stringUtils';
import { FilterMatchMode, FilterService } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import DatePicker, { DatePickerMonthChangeEvent } from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import InputTextAera from 'primevue/textarea';
import ModalSelectDate from '@/components/modal/ModalSelectDate.vue';
import PackageHotelRoomSelection from '@/components/tabs/PackageQuoteForm/PackageHotelRoomSelection.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';

interface Props {
  advancedForm: object;
  calendarData?: any;
  packageData?: any;
  validate?: any;
  isShowError?: boolean;
  durationDate?: any;
  dateValue?: any;
  listGatewaysByDest?: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  addOptionPackage: [];
  removeOptionPackage: [];
  addHotelPackage: [indexOption: number];
  removeHotelPackage: [indexOption: number];
  addRoomPackage: [indexOption: number, indexHotel: number];
  removeRoomPackage: [indexOption: number, indexHotel: number];
  updateDurationDate: [event: any, index: number, data: any];
  updateDateValue: [event: any, index: number, data: any];
  'update:packageData': [value: any];
}>();

const packageDataForm = computed({
  get() {
    return props.packageData;
  },
  set(value) {
    emits('update:packageData', value);
  }
});

const { getRoomCategoriesData, formatAirport, handleRedirectFlight, handleSetLimit, initHotelRoomPayload, depDateForm, checkIsAfterDate346, getQuoteDepartureData, getLimitDate } = useTabComposable();

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();

const currentLanguage = getCurrentLocale();
const adminSetupStore = useAdminSetupStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const hotelRoomCategoryData = new Map();
const gatewayStore = useGatewayStore();
const destinationStore = useDestinationStore();
const durationStore = useDurationStore();
const viewQuoteStore = useViewQuoteStore();
const hotelStore = useHotelStore();

const getRoomsLoadings = ref([[]]);
const minDateRangeValue = ref([new Date(DateTime.utc().plus({ days: 45 }))]);
const maxDateRangeValue = ref<(Date | undefined)[]>([new Date(DateTime.utc().plus({ days: 346 }))]);
const filterDestination = ref([]);
const filterGateway = ref();
const dateRef = ref(null);
const dateCurrentShow = ref({
  month: 0,
  year: 0
});
const dataSeasonalCalendar = ref<any>([]);
const disabledDays = ref([[]]);
const disableDates = ref([[]]);
const limitCalendar = ref({});
const maxOptionConfig = ref(1);
const maxHotelConfig = ref(1);
const maxRoomConfig = ref(1);
const today = new Date();
const currentDay = today.getDate();
today.setDate(currentDay + 346);

const durationsMultiple = computed(() => durationStore.selectDurationsMultiple);
const destinationSelected = computed(() => destinationStore.selectDestinationSelected);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const gateways = computed(() => {
  return formatAirport(gatewayStore.selectGateways, currentLanguage);
});
const packageOptionConfig = computed(() => adminSetupStore.optionConfig?.packageOptionConfig);

const gatewaysByDestination = ref([]);
const destinationsMultiple = computed(() => {
  return destinationStore.selectDestinationsMultiple;
});

const handleOpenModalError = (isAfterDate346 = false) => {
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      isAlignCenter: true,
      message: isAfterDate346 ? t('notification.both_land_package_plus_error') : t('notification.over_departure_date')
    },
    props: {
      modal: true,
      header: t('advance_quote.form.notice')
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.close'), onClick: () => modalRef.close() }))];
      }
    }
  });
};

const addOptionPackage = () => {
  emits('addOptionPackage');
};

const removeOptionPackage = () => {
  emits('removeOptionPackage');
};

const handleOpenModalDate = (listDate: any, callBackFn: any) => {
  const dialogRef = dialog.open(ModalSelectDate, {
    data: {
      availableDates: listDate
    },
    props: {
      modal: true,
      header: t('advance_quote.alternative_date'),
      style: {
        width: '560px'
      }
    },
    emits: {
      isSave: (dateSelected: any) => {
        dialogRef.close();
        callBackFn(dateSelected);
      }
    }
  });
};

const removeHotelPackage = (index: number) => {
  emits('removeHotelPackage', index);
};

const addHotelPackage = (index: number) => {
  emits('addHotelPackage', index);
};

const removeRoomPackage = (index: number, indexHotel: number) => {
  emits('removeRoomPackage', index, indexHotel);
};

const addRoomPackage = (index: number, indexHotel: number) => {
  emits('addRoomPackage', index, indexHotel);
};

const renderNewDepDatePackage = (depDate: any, indexPackage: number, response: any) => {
  if (response) {
    packageDataForm.value[indexPackage].dateRange = new Date(DateTime.fromISO(depDate));
    emits('updateDateValue', packageDataForm.value[indexPackage].dateRange, indexPackage, packageDataForm.value);
    emits('updateDurationDate', { value: packageDataForm.value[indexPackage].duration }, indexPackage, packageDataForm.value);
  }
};

const getListRoomPackageData = async (payload, indexPackage: number, hotelIndex: number, hotelSelects, isAfterDate346 = false) => {
  getRoomsLoadings.value[indexPackage][hotelIndex] = true;
  const gateway = packageDataForm.value[indexPackage].gateway;
  if (!hotelSelects || typeof hotelSelects !== 'object') {
    return;
  }
  hotelSelects.groupDes = destinationSelected?.value?.groupDes;
  checkIsAfterDate346(isAfterDate346);
  const limitData = {
    min: minDateRangeValue.value[0],
    max: maxDateRangeValue.value[0],
    isPackageForm: true
  };
  const response = await getRoomCategoriesData(payload, hotelSelects, hotelSelects?.excludeHotelRoom, gateway, handleOpenModalDate, handleOpenModalError, limitData);
  if (response === null) {
    getRoomsLoadings.value[indexPackage][hotelIndex] = false;
    return;
  }

  renderNewDepDatePackage(depDateForm.value, indexPackage, response);

  if (response?.data?.length) {
    if (hotelSelects?.excludeHotelRoom) {
      const listRoom = response.data?.filter((room) => !hotelSelects?.excludeHotelRoom.map((excludeRoom) => excludeRoom.toLowerCase()).includes(room.room?.toLowerCase()));
      hotelRoomCategoryData.set(payload.productCode, listRoom);
    } else {
      hotelRoomCategoryData.set(payload.productCode, response.data);
    }
  }
  if (!hotelRoomCategoryData.get(payload.productCode)?.length) {
    packageDataForm.value[indexPackage].hotels[hotelIndex].rooms.length = 1;
  }

  getRoomsLoadings.value[indexPackage][hotelIndex] = false;
};

const selectedGatewayPackage = async (e, index: number) => {
  if (index === 0) {
    packageDataForm.value[index].destination = '';
    packageDataForm.value[index].dateRange = undefined;
    dataSeasonalCalendar.value[index] = undefined;
    packageDataForm.value.forEach((packageItem, indexPackage) => {
      if (indexPackage > 0) {
        packageItem.gateway = '';
      }
    });
  } else {
    packageDataForm.value[index].dateRange = undefined;
    durationStore.updateDurationsMultiple({ durations: [], index });
    let durationList = [];
    gatewayStore.getGateWayDuration(packageDataForm.value[index]?.gateway?.code, packageDataForm.value[index]?.destination?.code, packageDataForm.value[index]?.dateRange).then((res) => {
      durationList = gatewayStore.getDurationByGatewayAndDestination(packageDataForm.value[index]?.gateway?.code, packageDataForm.value[index]?.destination?.code, res);
      if (durationList.length) {
        const durations = durationList.map((item) => {
          return { name: item };
        });
        durationStore.updateDurationsMultiple({ durations, index });
      }
    });
  }

  destinationStore.updateDestinationsMultiple({ destinations: [], index });

  hotelStore.updateHotelMultiple({ hotels: [], index });
  filterDestination.value = [];

  if (e.value?.code) {
    const listDestinationsId = await gatewayStore.getDestinationsByGateCode(e.value?.code);
    const payload = {
      listDestinationsId,
      index
    };
    await destinationStore.getDestinationsMultiple(payload, currentLanguage);
    filterDestination.value = [...destinationStore.selectDestinationsMultiple];
    packageDataForm.value.forEach((m, index) => {
      if (m?.gateway?.code && m?.destination?.airportCode) {
        getSeasionalCalendar(m, index);
      }
    });
  }
};

const getSeasionalCalendar = (data, index: number) => {
  destinationStore
    .getSeasonalCalendar({
      gateCode: data.gateway.code,
      destCode: data.destination.airportCode
    })
    .then((res) => {
      dataSeasonalCalendar.value[index] = res;
      checkSeasonalRule(props.advancedForm[0].groupTypeId, index);
    });
};

const selectedDestinationPackage = async (event, index: number) => {
  destinationStore.updateDestinationSelected(event.value);
  packageDataForm.value[index]?.hotels.forEach((hotel) => clearHotelPackage(hotel));
  packageDataForm.value[index].dateRange = undefined;
  if (event.value?.code) {
    gatewayStore.getGateWaysByDestination(event.value?.code).then((res) => {
      gatewaysByDestination.value = formatAirport(res, currentLanguage);
    });
    durationStore.updateDurationsMultiple({ durations: [], index });
    const gatewayDuration = await gatewayStore.getGateWayDuration(packageDataForm.value[index].gateway.code, packageDataForm.value[index].destination.code, packageDataForm.value[index].dateRange || '');
    const durationList = gatewayStore.getDurationByGatewayAndDestination(packageDataForm.value[index].gateway.code, packageDataForm.value[index].destination.code, gatewayDuration);

    if (durationList.length) {
      const durations = durationList.map((item) => {
        return { name: item };
      });
      durationStore.updateDurationsMultiple({ durations, index });
      packageDataForm.value.forEach((packageItem, indexPackage) => {
        if (indexPackage > 0) {
          durationStore.updateDurationsMultiple({ durations, index: indexPackage });
        }
      });
    }
    packageDataForm.value.forEach((packageItem: any, indexPackage) => {
      packageItem.destination = event.value;
      packageItem.brochure = event.value?.brochure;
      hotelStore.updateHotelMultiple({ hotels: [], index: indexPackage });
      if (event.value.code && packageItem?.dateRange && packageItem?.duration?.name) {
        getHotelListByDestinationPackage(event.value.code, indexPackage, packageItem);
      }
    });
    packageDataForm.value.forEach((m, index) => {
      if (m?.gateway?.code && m?.destination?.airportCode) {
        getSeasionalCalendar(m, index);
      }
    });
  }
};

const getHotelListByDestinationPackage = async (destinationCode: any, hotelIndex: any, packageItem: any) => {
  const payload = {
    quoteType: 'PACKAGE',
    departDate: packageItem?.dateRange,
    duration: packageItem?.duration?.name
  };

  const hotelIds = await hotelStore.getListHotelFormDestination(destinationCode);
  if (hotelIds.data?.length) {
    await hotelStore.getHotelMultiple({ hotelDurations: hotelIds.data, index: hotelIndex, data: payload });
  }
  return hotelIds;
};

const searchDestinationPackage = async (event, index: number) => {
  const query = event.query;
  const newFilteredDestinationPackage = [];
  if (!filterDestination.value?.length) {
    const listDestinationsId = await gatewayStore.getDestinationsByGateCode(packageDataForm.value[0].gateway?.code);
    const payloadData = {
      listDestinationsId,
      index: 0
    };
    await destinationStore.getDestinationsMultiple(payloadData, currentLanguage);
  }
  for (const destinationPackage of destinationsMultiple.value[index]) {
    const listItemFiltered = FilterService.filter(destinationPackage.items, ['name', 'nameFr', 'airportCode', 'country', 'countryFr'], query, FilterMatchMode.CONTAINS);
    if (listItemFiltered?.length) {
      newFilteredDestinationPackage.push({ ...destinationPackage, ...{ items: listItemFiltered } });
    }
  }
  filterDestination.value[index] = TabMixins.handleDuplicate(newFilteredDestinationPackage);
};

const handleFilter = (gatewayFilter, event) => {
  return gatewayFilter.filter((air) => {
    return (
      air.code?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      air.airportName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      air.name?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      air.region?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      air.countryName?.toLowerCase().startsWith(event.query.toLowerCase())
    );
  });
};

const onFilterGatewayPackage = async (event, index) => {
  if (!event.query.trim().length) {
    filterGateway.value = index === 0 ? [...gateways.value] : [...gatewaysByDestination.value];
  } else if (index === 0) {
    filterGateway.value = handleFilter(gateways.value, event);
  } else {
    filterGateway.value = handleFilter(gatewaysByDestination.value, event);
  }
};

const changeDatePackage = async (e: any, index: any, optionData: any) => {
  if (index === 0) {
    packageDataForm.value.forEach((packageItem, indexPackage) => {
      packageItem.dateRange = e;
      emits('updateDateValue', e, indexPackage, packageDataForm.value);
      if (packageDataForm.value[indexPackage].duration) {
        emits('updateDurationDate', { value: packageDataForm.value[indexPackage].duration }, indexPackage, packageDataForm.value);
      }
      optionData.hotels.forEach((hotel: any) => clearHotelPackage(hotel));
    });
  } else {
    emits('updateDateValue', e, index, packageDataForm.value);
    if (packageDataForm.value[index].duration) {
      emits('updateDurationDate', { value: packageDataForm.value[index].duration }, index, packageDataForm.value);
    }
  }
  if (optionData?.dateRange && optionData?.destination?.code && optionData?.duration?.name) {
    getHotelListByDestinationPackage(optionData?.destination?.code, index, optionData);
  }

  if (optionData?.dateRange && optionData?.destination?.code && optionData?.gateway?.code) {
    durationStore.updateDurationsMultiple({ durations: [], index });
    const gatewayDurationData = await gatewayStore.getGateWayDuration(optionData?.gateway?.code, optionData?.destination?.code, optionData?.dateRange);
    const durationListData = gatewayStore.getDurationByGatewayAndDestination(optionData?.gateway?.code, optionData?.destination?.code, gatewayDurationData);

    if (durationListData.length > 0) {
      const durations = durationListData?.map((item: any) => {
        return { name: item };
      });
      durationStore.updateDurationsMultiple({ durations, index });
      packageDataForm.value.forEach((packageItemData: any, indexPackageData: number) => {
        if (indexPackageData > 0) {
          durationStore.updateDurationsMultiple({ durations, index: indexPackageData });
        }
      });
    }
  }
  hotelStore.clearRoomCategory();
  hotelChangeRequote(optionData, index, 'date');
  conditionSelectDate(e, index);
};

const conditionSelectDate = (e: any, index: number) => {
  const selectDate = new Date(e);

  const lessThan346 = dataSeasonalCalendar.value[index] && dataSeasonalCalendar.value[index].lessThan346;
  const greaterThan346 = dataSeasonalCalendar.value[index] && dataSeasonalCalendar.value[index].greaterThan346;
  if (
    (selectDate.getDate() <= today.getDate() && selectDate?.getMonth() + 1 === today?.getMonth() + 1 && selectDate.getFullYear() === today.getFullYear()) ||
    (selectDate?.getMonth() + 1 <= today?.getMonth() + 1 && selectDate.getFullYear() === today.getFullYear()) ||
    selectDate.getFullYear() < today.getFullYear()
  ) {
    if (lessThan346) {
      getArrayDuration(lessThan346, selectDate, index);
    }
  } else if (greaterThan346) {
    getArrayDurationGreater(greaterThan346, selectDate, index);
  }
};

const getArrayDurationGreater = (sesionOption, selectDate, index) => {
  let combinedArray = [];
  if (sesionOption.exceptionSeason.months.includes(selectDate?.getMonth() + 1)) {
    combinedArray = Array.from(new Set([...combinedArray, ...sesionOption.exceptionSeason.durations]));
  }
  if (sesionOption.summerSeason.months.includes(selectDate?.getMonth() + 1)) {
    combinedArray = Array.from(new Set([...combinedArray, ...sesionOption.summerSeason.durations]));
  }
  if (sesionOption.winterSeason.months.includes(selectDate?.getMonth() + 1)) {
    combinedArray = Array.from(new Set([...combinedArray, ...sesionOption.winterSeason.durations]));
  }

  if (combinedArray.length > 0) {
    combinedArray.sort((a, b) => a - b);
    const arrayDuration = combinedArray.map((item) => ({ name: item }));
    durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
    return;
  }
  updateDurationStoreInMonth(sesionOption, selectDate, index);
};

const updateDurationStoreInMonth = (sesionOption, selectDate, index) => {
  let combinedArray = [];
  const monthFind = sesionOption.exceptionSeason.monthOther.find((m) => m.months === selectDate?.getMonth() + 1);

  if (monthFind) {
    if (monthFind.summerDay <= monthFind.winterDay) {
      if (selectDate.getDate() <= monthFind.summerDay) {
        combinedArray = Array.from(new Set([...combinedArray, ...sesionOption.summerSeason.durations]));
      } else if (selectDate.getDate() >= monthFind.winterDay) {
        combinedArray = Array.from(new Set([...combinedArray, ...sesionOption.winterSeason.durations]));
      }
    } else if (monthFind.summerDay >= monthFind.winterDay) {
      if (selectDate.getDate() <= monthFind.winterDay) {
        combinedArray = Array.from(new Set([...combinedArray, ...sesionOption.winterSeason.durations]));
      } else if (selectDate.getDate() >= monthFind.summerDay) {
        combinedArray = Array.from(new Set([...combinedArray, ...sesionOption.summerSeason.durations]));
      }
    }

    combinedArray.sort((a, b) => a - b);
    const arrayDuration = combinedArray.map((item) => ({ name: item }));
    durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
  }
};

const getArrayDuration = (sesionOption, selectDate, index) => {
  const durationsMultipleTemp = durationsMultiple.value[index].map((item) => item.name);
  if (sesionOption.exceptionSeason.months.includes(selectDate?.getMonth() + 1)) {
    const combinedArray = Array.from(new Set([...durationsMultipleTemp, ...sesionOption.exceptionSeason.durations]));
    combinedArray.sort((a, b) => a - b);
    const arrayDuration = combinedArray.map((item) => ({ name: item }));
    durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
    return;
  }
  if (sesionOption.summerSeason.months.includes(selectDate?.getMonth() + 1)) {
    const combinedArray = Array.from(new Set([...durationsMultipleTemp, ...sesionOption.summerSeason.durations]));
    combinedArray.sort((a, b) => a - b);
    const arrayDuration = combinedArray.map((item) => ({ name: item }));
    durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
    return;
  }
  if (sesionOption.winterSeason.months.includes(selectDate?.getMonth() + 1)) {
    const combinedArray = Array.from(new Set([...durationsMultipleTemp, ...sesionOption.winterSeason.durations]));
    combinedArray.sort((a, b) => a - b);
    const arrayDuration = combinedArray.map((item) => ({ name: item }));
    durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
    return;
  }
  const monthFind = sesionOption.exceptionSeason.monthOther.find((m) => m.months === selectDate?.getMonth() + 1);

  if (monthFind) {
    if (monthFind.summerDay <= monthFind.winterDay) {
      if (selectDate.getDate() <= monthFind.summerDay) {
        const combinedArray = Array.from(new Set([...durationsMultipleTemp, ...sesionOption.summerSeason.durations]));
        combinedArray.sort((a, b) => a - b);
        const arrayDuration = combinedArray.map((item) => ({ name: item }));
        durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
      } else if (selectDate.getDate() >= monthFind.winterDay) {
        const combinedArray = Array.from(new Set([...durationsMultipleTemp, ...sesionOption.winterSeason.durations]));
        combinedArray.sort((a, b) => a - b);
        const arrayDuration = combinedArray.map((item) => ({ name: item }));
        durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
      }
    } else if (monthFind.summerDay >= monthFind.winterDay) {
      if (selectDate.getDate() <= monthFind.winterDay) {
        const combinedArray = Array.from(new Set([...durationsMultipleTemp, ...sesionOption.winterSeason.durations]));
        combinedArray.sort((a, b) => a - b);
        const arrayDuration = combinedArray.map((item) => ({ name: item }));
        durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
      } else if (selectDate.getDate() >= monthFind.summerDay) {
        const combinedArray = Array.from(new Set([...durationsMultipleTemp, ...sesionOption.summerSeason.durations]));
        combinedArray.sort((a, b) => a - b);
        const arrayDuration = combinedArray.map((item) => ({ name: item }));
        durationStore.updateDurationsMultiple({ durations: arrayDuration, index });
      }
    }
  }
};

const changeDuration = (event: any, index: number, option: any) => {
  if (event.value?.name !== packageDataForm.value[index]?.duration?.name) {
    option.duration = event.value;
    hotelChangeRequote(option, index, 'duration');
  }
  option.duration.name = event.value.name;
  if (index === 0) {
    packageDataForm.value.forEach((packageItem: any, indexPackage: number) => {
      packageItem.duration = event.value;
      emits('updateDurationDate', event, indexPackage, packageDataForm.value);
    });
    option.hotels.forEach((hotel: any) => clearHotelPackage(hotel));
  } else {
    emits('updateDurationDate', event, index, packageDataForm.value);
  }

  if (option?.dateRange && option?.destination?.code && option?.duration?.name) {
    getHotelListByDestinationPackage(option?.destination?.code, index, option);
  }
  hotelStore.clearRoomCategory();
};

const hotelChangeRequote = (e, index, type) => {
  packageDataForm.value[index].hotels.forEach((hotel, hotelIndex) => {
    if (packageDataForm.value[index].dateRange && hotel.hotel?.hCode && isRequote.value) {
      const payload = initHotelRoomPayload(packageDataForm.value[index], e, type, props.dateValue?.[index], props.durationDate?.[index], hotel.hotel?.hCode);
      getListRoomPackageData(payload, index, hotelIndex, hotel.hotel);
    }
  });
};

const changeAdultPackage = (event, option, index) => {
  if (event !== packageDataForm.value[index]?.adults) {
    option.adults = event;
    hotelChangeRequote(option, index, 'adult');
  }
  option.adults = event;
  setNumericCharacters(option, 'adults');
};

const setNumericCharacters = (option, name) => {
  if (option[name] && option[name].toString().length > 3) {
    option[name] = 999;
  }
};

const clearGateway = (index: number) => {
  packageDataForm.value[index].gateway = '';
  if (index === 0) {
    packageDataForm.value[index].dateRange = undefined;
    dataSeasonalCalendar.value[index] = undefined;
    packageDataForm.value[index].duration = '';
    clearDestination();
  }
};

const clearDestination = () => {
  packageDataForm.value.forEach((packageItem: any, index: number) => {
    packageDataForm.value[index].dateRange = undefined;
    dataSeasonalCalendar.value[index] = undefined;
    packageItem.destination = '';
    packageItem.duration = '';
    if (index == 0) {
      packageItem?.hotels.forEach((hotel) => clearHotelPackage(hotel));
    }
    if (packageItem > 0) {
      packageItem.gateway = '';
    }
  });
};

const clearHotelPackage = (hotel) => {
  hotel.hotel = '';
  hotel.rooms = clearHotelRoomPackage(hotel.rooms);
  hotelRoomCategoryData.clear();
};

const clearHotelRoomPackage = (rooms) => {
  return rooms.map(() => {
    return {
      room: ''
    };
  });
};

const getDisabledDates = (month, year, season) => {
  const disabledDates = [];
  const daysInMonth = new Date(year, month, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month - 1, day);
    if (season.summerDay < season.winterDay && date?.getMonth() + 1 === season.months && day <= season.summerDay) {
      if ((day <= season.summerDay && season?.daysOfWeekDisableSummer?.includes(date.getDay())) || (day >= season.winterDay && season?.daysOfWeekDisableWinter?.includes(date.getDay()))) {
        disabledDates.push(day);
      }
    } else if ((day <= season.winterDay && season?.daysOfWeekDisableWinter?.includes(date.getDay())) || (day >= season.summerDay && season?.daysOfWeekDisableSummer?.includes(date.getDay()))) {
      disabledDates.push(day);
    }
  }
  return disabledDates;
};

const pushDisableDates = (year, month, day, index: number) => {
  disableDates.value[index].push(new Date(`${year}-${month}-${day}`));
};

const handleDisableDatesGreaterThan = (greaterThan346, year, month, day, date, index: number) => {
  if (greaterThan346?.exceptionSeason?.months.includes(month)) {
    if (greaterThan346?.exceptionSeason?.daysOfWeekDisable.includes(date.getDay())) {
      pushDisableDates(year, month, day, index);
    }
    return;
  }
  if (greaterThan346?.summerSeason?.months.includes(month) && greaterThan346.summerSeason.daysOfWeekDisable.includes(date.getDay())) {
    pushDisableDates(year, month, day, index);
  }
  if (greaterThan346?.winterSeason?.months.includes(month) && greaterThan346.winterSeason.daysOfWeekDisable.includes(date.getDay())) {
    pushDisableDates(year, month, day, index);
  }
  const monthFind = greaterThan346?.exceptionSeason?.monthOther.find((m) => {
    return m.months === month;
  });

  if (monthFind) {
    const disabledDates1 = getDisabledDates(month, year, monthFind);
    if (disabledDates1.includes(day)) {
      pushDisableDates(year, month, day, index);
    }
  }
};

const handleDisableDatesLessThan = (lessThan346, year, month, day, date, index: number) => {
  if (lessThan346.exceptionSeason.months.includes(month)) {
    if (lessThan346.exceptionSeason.daysOfWeekDisable.includes(date.getDay())) {
      pushDisableDates(year, month, day, index);
    }
    return;
  }
  if (lessThan346.summerSeason.months.includes(month) && lessThan346.summerSeason.daysOfWeekDisable.includes(date.getDay())) {
    pushDisableDates(year, month, day, index);
  }
  if (lessThan346.winterSeason.months.includes(month) && lessThan346.winterSeason.daysOfWeekDisable.includes(date.getDay())) {
    pushDisableDates(year, month, day, index);
  }
  const monthFind = lessThan346.exceptionSeason.monthOther.find((m) => m.month === month);

  if (monthFind) {
    const disabledDates1 = getDisabledDates(month, year, monthFind);
    if (disabledDates1.includes(day)) {
      pushDisableDates(year, month, day, index);
    }
  }
};

const handleSeasonalCalendarDefault = (seasonalCalendarDefault, month, index: number) => {
  if (seasonalCalendarDefault.exceptionSeason?.months && seasonalCalendarDefault.exceptionSeason?.months.includes(month)) {
    disabledDays.value[index].push(...seasonalCalendarDefault.exceptionSeason.daysOfWeekDisable);
    return;
  }
  if (seasonalCalendarDefault.summerSeason?.months.includes(month)) {
    disabledDays.value[index].push(...seasonalCalendarDefault.summerSeason.daysOfWeekDisable);
  }
  if (seasonalCalendarDefault.winterSeason?.months.includes(month)) {
    disabledDays.value[index].push(...seasonalCalendarDefault.winterSeason.daysOfWeekDisable);
  }
};

const onMonthChange = (event: DatePickerMonthChangeEvent, index: number) => {
  const month = event.month;
  const year = event.year;
  const lessThan346 = dataSeasonalCalendar.value[index].lessThan346;
  const greaterThan346 = dataSeasonalCalendar.value[index].greaterThan346;
  dateCurrentShow.value = event;
  disabledDays.value[index] = [];
  disableDates.value[index] = [];
  let seasonalCalendarDefault = lessThan346;

  if (month === today?.getMonth() + 1 && year === today.getFullYear()) {
    const daysInMonth = new Date(year, month, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(`${year}-${month}-${day}`);
      processHandleDisableDates(lessThan346, greaterThan346, year, month, day, date, index);
      processHandleDisableDatesAfterToday(lessThan346, greaterThan346, year, month, day, date, index);
      processHandleDisableDatesBeforeToday(lessThan346, greaterThan346, year, month, day, date, index);
    }
    return;
  }

  if (conditionSeasonal(year, month, greaterThan346)) {
    seasonalCalendarDefault = greaterThan346;
  }
  disableAllDateAfterToday(year, month, greaterThan346, index);

  if (seasonalCalendarDefault) {
    if (disabledDays.value[index].length) {
      return;
    }
    handleSeasonalCalendarDefault(seasonalCalendarDefault, month, index);
    if (disabledDays.value[index].length) {
      return;
    }
    setDisableDatesValue(seasonalCalendarDefault, year, month, index);
  }
};

const disableAllDateAfterToday = (year, month, greaterThan346, index: number) => {
  if ((month > today?.getMonth() + 1 && year === today.getFullYear() && !greaterThan346) || (year > today.getFullYear() && !greaterThan346)) {
    disabledDays.value[index] = [0, 1, 2, 3, 4, 5, 6];
  }
};

const processHandleDisableDates = (lessThan346, greaterThan346, year, month, day, date, index: number) => {
  if (lessThan346 && greaterThan346) {
    if (day <= today.getDate()) {
      handleDisableDatesLessThan(lessThan346, year, month, day, date, index);
    } else {
      handleDisableDatesGreaterThan(greaterThan346, year, month, day, date, index);
    }
  }
};

const processHandleDisableDatesAfterToday = (lessThan346, greaterThan346, year, month, day, date, index: number) => {
  if (!lessThan346 && greaterThan346 && day > today.getDate()) {
    handleDisableDatesGreaterThan(greaterThan346, year, month, day, date, index);
  }
};

const processHandleDisableDatesBeforeToday = (lessThan346, greaterThan346, year, month, day, date, index: number) => {
  if (lessThan346 && !greaterThan346) {
    if (day <= today.getDate()) {
      handleDisableDatesGreaterThan(lessThan346, year, month, day, date, index);
    } else {
      pushDisableDates(year, month, day, index);
    }
  }
};

const setDisableDatesValue = (seasonalCalendarDefault, year, month, index: number) => {
  const monthFind = seasonalCalendarDefault.exceptionSeason.monthOther.find((m) => m.months === month);
  if (monthFind) {
    const disabledDates1 = getDisabledDates(month, year, monthFind);
    const monthFindConvert = disabledDates1?.map((m) => new Date(`${year}-${month}-${m}`));
    disableDates.value[index] = monthFindConvert;
  }
};

const conditionSeasonal = (year, month, greaterThan346) => {
  return (month > today?.getMonth() + 1 && year === today.getFullYear() && greaterThan346) || (year > today.getFullYear() && greaterThan346);
};

const onShow = (index: number) => {
  const year = dateCurrentShow.value.year ? dateCurrentShow.value.year : new Date(minDateRangeValue.value[index]).getFullYear();
  const month = dateCurrentShow.value.month ? dateCurrentShow.value.month : new Date(minDateRangeValue.value[index])?.getMonth() + 1;
  const date = {
    month: month,
    year: year
  };
  onMonthChange(date, index);
};

const checkSeasonalRule = (advancedFormPackage: any, indexPackage: number) => {
  const greaterThan346 = dataSeasonalCalendar.value[indexPackage] && dataSeasonalCalendar.value[indexPackage].greaterThan346;
  if (greaterThan346 && Object.keys(greaterThan346).length > 0) {
    handleDateLimitationPackage(advancedFormPackage, indexPackage);
  } else {
    minDateRangeValue.value[indexPackage] = isWorksheetRequote.value ? new Date(DateTime.utc().plus({ days: 1 })) : getLimitDate(advancedFormPackage, limitCalendar.value, 'startDateConfig');
    maxDateRangeValue.value[indexPackage] = isWorksheetRequote.value ? undefined : new Date(DateTime.utc().plus({ days: 346 }));
  }
};

const handleDateLimitationPackage = (advancedFormPackage: any, indexPackage: number) => {
  if (advancedFormPackage) {
    minDateRangeValue.value[indexPackage] = isWorksheetRequote.value ? new Date(DateTime.utc().plus({ days: 1 })) : getLimitDate(advancedFormPackage, limitCalendar.value, 'startDateConfig');
    maxDateRangeValue.value[indexPackage] = isWorksheetRequote.value ? undefined : getLimitDate(advancedFormPackage, limitCalendar.value, 'endDateConfig');
  }
};

const updateDurationDate = (event: any, index: number, data: any) => {
  emits('updateDurationDate', event, index, data);
};

const updateDateValue = (event: any, index: number, data: any) => {
  emits('updateDateValue', event, index, data);
};

const handleConfigReplicateQuote = () => {
  if (packageDataForm.value.length > maxOptionConfig.value) {
    maxOptionConfig.value = packageDataForm.value.length;
  }
  packageDataForm.value.forEach((option: any) => {
    if (option.hotels.length > maxHotelConfig.value) {
      maxHotelConfig.value = option.hotels.length;
    }

    option.hotels.forEach((hotel: any) => {
      if (hotel.rooms.length > maxRoomConfig.value) {
        maxRoomConfig.value = hotel.rooms.length;
      }
    });
  });
};

const handleClear = (maxOption: number, maxHotel: number, maxRoom: number) => {
  if (isRequote.value && isWorksheetRequote.value) {
    handleConfigReplicateQuote();
    return;
  }
  if (maxOption && packageDataForm.value.length && maxOption < packageDataForm.value.length) {
    packageDataForm.value.splice(maxOption);
  }

  packageDataForm.value.forEach((option: any) => {
    if (Array.isArray(option.hotels)) {
      if (maxHotel && option.hotels.length > maxHotel) {
        option.hotels.splice(maxHotel);
      }

      option.hotels.forEach((hotel: any) => {
        if (Array.isArray(hotel.rooms)) {
          if (maxRoom && hotel.rooms.length > maxRoom) {
            hotel.rooms.splice(maxRoom);
          }
        }
      });
    }
  });
};

const handleChangeGroupType = (advancedForm: any, config: any) => {
  if (advancedForm[0].groupType) {
    maxOptionConfig.value = handleSetLimit(advancedForm, config).maxOption;
    maxHotelConfig.value = handleSetLimit(advancedForm, config).maxHotel;
    maxRoomConfig.value = handleSetLimit(advancedForm, config).maxRoom;
    handleClear(handleSetLimit(advancedForm, config).maxOption, handleSetLimit(advancedForm, config).maxHotel, handleSetLimit(advancedForm, config).maxRoom);
  }
};

watch(packageDataForm.value, () => {
  packageDataForm.value.forEach((pack, index) => {
    if (pack.gateway?.code && pack.destination?.airportCode) {
      getSeasionalCalendar(pack, index);
    }
  });
});

watch(
  () => packageDataForm.value,
  (newValue) => {
    newValue.forEach((packageVal) => {
      if (!packageVal.adults) {
        packageVal.adults = 0;
      }
      if (!packageVal.children) {
        packageVal.children = 0;
      }
      if (!packageVal.numOfRooms) {
        packageVal.numOfRooms = 0;
      }
    });
  },
  { deep: true }
);

watch(
  () => props.advancedForm,
  (newValue) => {
    if (newValue) {
      packageDataForm.value.forEach((pack: any, index: number) => {
        pack.dateRange = undefined;
        if (pack.gateway?.code && pack.destination?.airportCode) {
          checkSeasonalRule(newValue[0].groupTypeId, index);
        }
        pack.hotels.forEach((hotel: any) => {
          clearHotelPackage(hotel);
        });
      });
      handleChangeGroupType(newValue, packageOptionConfig.value);
    }
  },
  { deep: true }
);

watch(
  () => props.calendarData,
  (newValue) => {
    if (newValue) {
      limitCalendar.value = getQuoteDepartureData(newValue);
    }
  },
  { deep: true }
);

watch(
  () => props.listGatewaysByDest,
  (newValue) => {
    if (newValue) {
      gatewaysByDestination.value = cloneDeep(newValue);
    }
  },
  { deep: true, immediate: true }
);
</script>
<template>
  <div class="package-form-option">
    <div class="grid" v-for="(option, index) in packageDataForm" :key="index">
      <div class="col">
        <p>{{ $t('common.option') }} {{ index + 1 }}</p>
        <p id="info-flight-link" @click="handleRedirectFlight()" class="cursor-pointer flex gap-2 align-items-center justify-content-end"><i class="text-3xl pi pi-exclamation-circle exclamation-icon"></i> {{ $t('common.where_we_fly') }}</p>
        <div class="grid">
          <div class="col-12 md:col-6">
            <Fluid>
              <div class="field">
                <label for="gateway" class="long-text" v-tooltip.top="$t('advance_quote.form.gateway')">{{ $t('advance_quote.form.gateway') }}</label>
                <div class="flex align-items-center">
                  <AutoComplete
                    v-model="option.gateway"
                    :suggestions="filterGateway"
                    @complete="onFilterGatewayPackage($event, index)"
                    :placeholder="$t('advance_quote.form.gateway')"
                    optionLabel="airportName"
                    completeOnFocus
                    @change="selectedGatewayPackage($event, index)"
                    :class="isShowError && validate[index]?.gateway[0]?.$message ? 'p-invalid' : ''"
                    class="w-full"
                    forceSelection
                  >
                    <template #option="slotProps">
                      <div class="flex align-options-center">
                        <div>
                          <div>{{ slotProps.option.airportName }} ({{ slotProps.option.code }})</div>
                          <div class="text-xs">{{ slotProps.option.name }}, {{ slotProps.option.region || slotProps.option.province }}, {{ slotProps.option.countryName }}</div>
                        </div>
                      </div>
                    </template>
                  </AutoComplete>
                  <div>
                    <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearGateway(index)"></i>
                  </div>
                </div>
                <span class="text-red-500" v-if="isShowError">
                  {{ validate[index]?.gateway[0]?.$message }}
                </span>
              </div>
            </Fluid>
          </div>
          <div class="col-12 md:col-6">
            <Fluid>
              <div class="field">
                <label for="destination" class="long-text" v-tooltip.top="$t('advance_quote.form.destination')">{{ $t('advance_quote.form.destination') }}</label>
                <div class="flex align-items-center">
                  <AutoComplete
                    class="w-full"
                    v-model="option.destination"
                    :suggestions="filterDestination[index]"
                    @complete="searchDestinationPackage($event, index)"
                    :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    completeOnFocus
                    @change="selectedDestinationPackage($event, index)"
                    :disabled="!option.gateway || index !== 0"
                    forceSelection
                    :class="isShowError && validate[index]?.destination[0]?.$message ? 'p-invalid' : ''"
                  >
                    <template #option="slotProps">
                      <div class="ml-7">{{ getTextVariant(currentLanguage, `${slotProps.option.name} (${slotProps.option.country})`, `${slotProps.option.nameFr} (${slotProps.option.countryFr})`) }}</div>
                    </template>
                  </AutoComplete>
                  <div v-if="index === 0">
                    <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearDestination()"></i>
                  </div>
                </div>
                <span class="text-red-500" v-if="isShowError">
                  {{ validate[index]?.destination[0]?.$message }}
                </span>
              </div>
            </Fluid>
          </div>
        </div>
        <div class="grid">
          <div class="col-12 lg:col-3 md:col-6">
            <Fluid>
              <div class="field">
                <label for="dataRange1" class="head-size" v-tooltip.top="$t('create_quote.departure_date')">{{ $t('create_quote.departure_date') }}</label>
                <DatePicker
                  ref="dateRef"
                  v-model="option.dateRange"
                  id="dataRange"
                  dateFormat="dd-M-yy"
                  :minDate="minDateRangeValue[index]"
                  :maxDate="maxDateRangeValue[index]"
                  :class="isShowError && validate[index]?.dateRange[0]?.$message ? 'p-invalid' : ''"
                  @update:modelValue="changeDatePackage($event, index, option)"
                  :selectOtherMonths="false"
                  :placeholder="$t('common.date_format')"
                  :disabledDays="disabledDays[index]"
                  :disabledDates="disableDates[index]"
                  @month-change="onMonthChange($event, index)"
                  @show="onShow(index)"
                  :disabled="!dataSeasonalCalendar[index]"
                >
                  <template #date="slotProps">
                    <strong v-if="!slotProps.date.selectable" class="line-through">{{ slotProps.date.day }}</strong>
                    <template v-else>{{ slotProps.date.day }}</template>
                  </template>
                  <template #footer>
                    <p class="text-center">
                      <small>{{ $t('common.press_space') }}</small>
                    </p>
                  </template>
                </DatePicker>
                <span class="text-red-500" v-if="isShowError">
                  {{ validate[index]?.dateRange[0]?.$message }}
                </span>
              </div>
            </Fluid>
          </div>
          <div class="col-12 lg:col-3 md:col-6">
            <Fluid>
              <div class="field">
                <label for="duration" class="head-size" v-tooltip.top="$t('advance_quote.form.duration')">{{ $t('advance_quote.form.duration') }}</label>
                <Select
                  :modelValue="option.dateRange ? option.duration : null"
                  :options="durationsMultiple[index]"
                  optionLabel="name"
                  :placeholder="$t('advance_quote.form.duration')"
                  id="destination"
                  :disabled="!option.dateRange || !dataSeasonalCalendar[index]"
                  :class="isShowError && validate[index]?.duration[0]?.$message ? 'p-invalid w-full' : 'w-full'"
                  @change="changeDuration($event, index, option)"
                />
                <span class="text-red-500" v-if="isShowError">
                  {{ validate[index]?.duration[0]?.$message }}
                </span>
              </div>
            </Fluid>
          </div>
          <div class="col-12 lg:col-2 md:col-4">
            <Fluid>
              <div class="field">
                <label for="number_room" class="head-size long-text" v-tooltip.top="$t('advance_quote.form.number_of_room')">{{ $t('advance_quote.form.nb_of_room') }}</label>
                <InputNumber :min="0" id="number_room" type="text" :class="isShowError && validate[index]?.numOfRooms[0]?.$message ? 'p-invalid' : ''" v-model="option.numOfRooms" />
                <span class="text-red-500" v-if="isShowError">
                  {{ validate[index]?.numOfRooms[0]?.$message }}
                </span>
              </div>
            </Fluid>
          </div>
          <div class="col-12 lg:col-2 md:col-4">
            <Fluid>
              <div class="field">
                <label for="adt" class="head-size" v-tooltip.top="$t('advance_quote.form.adult')">{{ $t('advance_quote.form.adult') }}</label>
                <InputNumber :min="0" id="adt" type="text" :modelValue="option.adults" :class="isShowError && validate[index]?.adults[0]?.$message ? 'p-invalid' : ''" @update:modelValue="changeAdultPackage($event, option, index)" />
                <span class="text-red-500" v-if="isShowError">
                  {{ validate[index]?.adults[0]?.$message }}
                </span>
              </div>
            </Fluid>
          </div>
          <div class="col-12 lg:col-2 md:col-4">
            <Fluid>
              <div class="field">
                <label for="chd" class="head-size long-text" v-tooltip.top="$t('advance_quote.form.children')">{{ $t('advance_quote.form.children_short') }}</label>
                <InputNumber :min="0" id="chd" type="text" v-model="option.children" />
              </div>
            </Fluid>
          </div>
        </div>
        <div>
          <PackageHotelRoomSelection
            :advancedForm="advancedForm"
            v-model:packageData="packageDataForm"
            :indexOption="index"
            :validate="validate[index]"
            :isShowError="isShowError"
            :minDateRangeValue="minDateRangeValue"
            :maxDateRangeValue="maxDateRangeValue"
            :durationDate="durationDate"
            :dateValue="dateValue"
            :maxHotelConfig="maxHotelConfig"
            :maxRoomConfig="maxRoomConfig"
            @updateDurationDate="(event, index, data) => updateDurationDate(event, index, data)"
            @updateDateValue="(event, index, data) => updateDateValue(event, index, data)"
            @addHotelPackage="(indexOption) => addHotelPackage(indexOption)"
            @removeHotelPackage="(indexOption) => removeHotelPackage(indexOption)"
            @addRoomPackage="(indexOption, indexHotel) => addRoomPackage(indexOption, indexHotel)"
            @removeRoomPackage="(indexOption, indexHotel) => removeRoomPackage(indexOption, indexHotel)"
          />
        </div>
        <Fluid v-if="authorizations?.advance_quote?.additional_note_view">
          <div class="field flex-col">
            <label for="additionalNote">{{ $t('advance_quote.form.additional_note') }}</label>
            <InputTextAera v-model="option.additionalNote" class="not-resize" :autoResize="true" rows="5" cols="30" maxlength="500" />
          </div>
        </Fluid>
      </div>
    </div>
    <div class="grid">
      <div class="flex w-full justify-content-around mb-3">
        <div href="javascript:" class="block text-primary cursor-pointer remove-option" @click="removeOptionPackage()" v-if="packageDataForm?.length > 1">- {{ $t('advance_quote.form.remove_option') }}</div>
        <div href="javascript:" class="block text-primary cursor-pointer add-option" v-if="packageDataForm?.length < maxOptionConfig" @click="addOptionPackage()">+ {{ $t('advance_quote.form.add_option') }}</div>
      </div>
    </div>
  </div>
</template>
